import { Box, Typography } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import AppButton from "../../../base/AppButton.component";
import { useAuth } from "../../../../context/Auth.context";
import { FETCH_WARRANTY_RESPONSE } from "../../../../graphql/query/query";
import {
  DEFAULT_ANNUAL_MILES,
  DEFAULT_YEARS_OWNED,
} from "../../../../utils/warrantyChart.utils";
import WarrantyChartView from "../../warranty/WarrantyChartView.component";
import { PopupStyles } from "./CustomerDetailsStyles";

/**
 * Customer Details → Warranty tab.
 * Shows the saved coverage timeline (read-only) with a Print button.
 * Prints the on-screen chart (same approach as ApiTable) so content is not blank.
 */
const WarrantyService = ({ customer }) => {
  const auth = useAuth();
  const printRef = useRef(null);

  const { data, loading } = useQuery(FETCH_WARRANTY_RESPONSE, {
    variables: {
      warrantyResponseFetchInput: {
        dealership_id: auth.user.dealership_id,
        user_id: customer?.customer?.user_id,
        employee_user_id: customer?.employee?.employee_user_id || null,
      },
    },
    fetchPolicy: "network-only",
    skip: !customer?.customer?.user_id || !auth?.user?.dealership_id,
  });

  const payload = data?.fetchWarrantyResponse;
  const response = payload?.response;
  const coverages = payload?.coverages || [];
  const exclusions = payload?.exclusions || [];

  const annualMiles = response?.annual_mileage ?? DEFAULT_ANNUAL_MILES;
  const yearsOwned = response?.years_owned ?? DEFAULT_YEARS_OWNED;
  const savedItems = useMemo(() => response?.items || [], [response?.items]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Warranty Coverage Timeline",
    pageStyle: `
      @page { size: landscape; margin: 10mm; }
      html, body {
        height: auto !important;
        overflow: visible !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      .warranty-no-print {
        display: none !important;
      }
    `,
  });

  if (loading) {
    return (
      <Box sx={PopupStyles.loadingStyle}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!response) {
    return (
      <Box sx={PopupStyles.loadingStyle}>
        <Typography>No warranty data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        ref={printRef}
        sx={{
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          background: "#fff",
          minHeight: 360,
          width: "100%",
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <WarrantyChartView
          coverages={coverages}
          exclusions={exclusions}
          annualMiles={annualMiles}
          yearsOwned={yearsOwned}
          savedItems={savedItems}
          isSpanish={false}
          readOnly
          animate={false}
          compact
          headerAction={
            <Box className="warranty-no-print">
              <AppButton
                buttonText="Print"
                variant="contained"
                size="small"
                clickHandler={handlePrint}
                sx={{ minWidth: 80 }}
              />
            </Box>
          }
          sx={{
            height: "auto",
            minHeight: 360,
            overflow: "visible",
            p: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default WarrantyService;
